import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Box, Button, CircularProgress, Snackbar, Typography, makeStyles
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import api from '../../api/client'
import { gregorianToJalali, thousandsSeparate, toFarsiDigits } from '../../assets/utils'
import ResponsiveTable from './ResponsiveTable'
import { LoadMoreButton, useIncrementalList } from '../../hooks/useIncrementalList'

const useStyles = makeStyles((theme) => ({
  filters: {
    padding: '10px 20px',
    background: '#f5f5f5',
    borderRadius: '4px',
    marginBottom: '10px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  filterRow: {
    display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap',
  },
  filterLabel: { fontWeight: 'bold', minWidth: '140px' },
  chipBtn: {
    margin: '0 4px',
    minWidth: '72px',
    background: '#e0e0e0',
    color: '#37474f',
    '&:hover': { background: '#bdbdbd' },
  },
  chipActive: {
    margin: '0 4px',
    minWidth: '72px',
    background: '#8fa8f8',
    color: '#fff',
    '&:hover': { background: '#3963f0' },
  },
  radioLabel: {
    display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
    padding: '6px 14px', borderRadius: '4px', border: '2px solid #ddd',
  },
  radioLabelActive: {
    display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer',
    padding: '6px 14px', borderRadius: '4px', border: '2px solid #2196f3',
    background: '#e3f2fd',
  },
  countRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    flexWrap: 'wrap', gap: 8,
  },
  printBtn: {
    background: '#4caf50', color: '#fff', padding: '8px 25px',
    fontSize: '14px', fontWeight: 'bold', '&:hover': { background: '#43a047' },
  },
  excelBtn: {
    background: '#0277bd', color: '#fff', padding: '8px 25px',
    fontSize: '14px', fontWeight: 'bold', '&:hover': { background: '#01579b' },
  },
}))

const fmtDate = (d) => (d ? toFarsiDigits(gregorianToJalali(d)) : '—')
const fmtMoney = (v) =>
  (v === null || v === undefined || v === '' ? '—' : toFarsiDigits(thousandsSeparate(v)))

/**
 * «گزارش کلیِ صورت وضعیتِ قرارداد» — همان چیزی که پیمانکار در پنلِ خود دارد:
 * انتخابِ چند گزینه (نوع / سال مالی / سند) و سپس «چاپ همه» یا «دانلود اکسل».
 *
 * داده‌ها از یک endpoint می‌آید ⇒ شمارنده دقیقاً همان عددی است که در PDF چاپ می‌شود.
 *
 * props:
 *   contract : قرارداد (id و number)
 *   onOpenRequest : (row) => void  — برای دکمهٔ «گزارش» هر ردیف
 */
export default function ContractReportPanel({ contract, onOpenRequest }) {
  const classes = useStyles()
  const contractId = contract?.id

  const [type, setType] = useState('همه')
  const [year, setYear] = useState('همه')
  const [doc, setDoc] = useState('همه')
  const [withInterest, setWithInterest] = useState(false)

  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState('')
  const [fileError, setFileError] = useState('')
  const [busy, setBusy] = useState(false)

  const filterParams = useMemo(() => {
    const p = {};
    if (type && type !== 'همه') p.type = type;
    if (year && year !== 'همه') p.year = year;
    if (doc === 'با سند') p.doc = 'with';
    else if (doc === 'بدون سند') p.doc = 'without';
    return p;
  }, [type, year, doc])

  // بارگذاریِ داده‌های گزارش (فیلترها + ردیف‌ها + شمارنده)
  const load = useCallback(async () => {
    if (!contractId) return
    setLoading(true)
    setErr('')
    try {
      const res = await api.get(`/contracts/${contractId}/report/data/`, {
        params: { ...filterParams, _ts: Date.now() },
      })
      setData(res.data)
    } catch (e) {
      setErr(
        (e && e.response && e.response.status === 403
          ? 'پیمانکارِ این قرارداد مشترک نیست؛ اطلاعاتِ صورت وضعیت نمایش داده نمی‌شود.'
          : (e && e.response && (e.response.data?.detail || e.response.data?.error))
            || 'خطا در دریافت گزارشِ صورت وضعیت')
      )
      setData(null)
    } finally {
      setLoading(false)
    }
  }, [contractId, filterParams])

  useEffect(() => { load() }, [load])

  // با تغییرِ فیلتر، انتخابِ نامعتبر نباید بماند
  useEffect(() => {
    const opts = data?.options
    if (!opts) return
    if (type !== 'همه' && !opts.types.includes(type)) setType('همه')
    if (year !== 'همه' && !opts.years.includes(String(year))) setYear('همه')
  }, [data]) // eslint-disable-line react-hooks/exhaustive-deps

  const rows = data?.rows || []
  const count = data?.count || 0
  const types = data?.options?.types || ['همه']
  const years = data?.options?.years || ['همه']

  // رندرِ تدریجی — روی موبایل با گزارش‌های چندهزار ردیفی کند نمی‌شود
  const { visible: visibleRows, hasMore, loadMore, remaining } = useIncrementalList(rows, {
    step: 50,
    resetKey: `${type}|${year}|${doc}`,
  })

  const reportColumns = useMemo(() => {
    const cols = [
      { key: 'type', label: 'نوع', primary: true, render: (r) => toFarsiDigits(r.type) },
      { key: 'number', label: 'شماره', primary: true, render: (r) => toFarsiDigits(r.number) },
      { key: 'letter_date', label: 'تاریخ نامه', render: (r) => fmtDate(r.letter_date) },
      { key: 'amount', label: 'مبلغ کارکرد', render: (r) => fmtMoney(r.amount) },
      { key: 'tax', label: 'مالیات بر ارزش افزوده', render: (r) => fmtMoney(r.tax) },
      { key: 'insurance', label: 'بیمه سهم دولت', render: (r) => fmtMoney(r.insurance) },
      { key: 'sum_amount', label: 'جمع کل', render: (r) => fmtMoney(r.sum_amount) },
      { key: 'insurance_deduction', label: 'کسور بیمه', render: (r) => fmtMoney(r.insurance_deduction) },
      { key: 'deposit_deduction', label: 'کسور سپرده', render: (r) => fmtMoney(r.deposit_deduction) },
      { key: 'other_deduction', label: 'سایر کسورات', render: (r) => fmtMoney(r.other_deduction) },
      { key: 'sum_deduction', label: 'جمع کسورات', render: (r) => fmtMoney(r.sum_deduction) },
      { key: 'net_amount', label: 'خالص صورت وضعیت', render: (r) => fmtMoney(r.net_amount) },
      { key: 'transfer_cash_amount', label: 'حواله نقدی', render: (r) => fmtMoney(r.transfer_cash_amount) },
      { key: 'transfer_amount', label: 'حواله اسناد خزانه', render: (r) => fmtMoney(r.transfer_amount) },
      { key: 'transfer_sum', label: 'جمع حواله', render: (r) => fmtMoney(r.transfer_sum) },
      { key: 'remainder', label: 'مانده صورت وضعیت', render: (r) => fmtMoney(r.remainder) },
      { key: 'transfer_interest', label: 'سود حواله', render: (r) => fmtMoney(r.transfer_interest) },
    ]
    if (onOpenRequest) {
      cols.push({
        key: 'actions',
        label: 'گزارش',
        render: (r) => (
          <Button
            size="small"
            variant="contained"
            color="primary"
            style={{ fontWeight: 700, fontSize: 11.5 }}
            onClick={() => onOpenRequest(r)}
          >
            گزارش
          </Button>
        ),
      })
    }
    return cols
  }, [onOpenRequest])

  const authHeaders = () => {
    const token =
      localStorage.getItem('access') ||
      localStorage.getItem('access_token') ||
      localStorage.getItem('token')
    return {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Cache-Control': 'no-store',
      Pragma: 'no-cache',
    }
  }

  const downloadFile = async (kind) => {
    if (!contractId) return
    setBusy(true)
    setFileError('')
    try {
      const params = { ...filterParams, _ts: Date.now() }
      if (withInterest) params.with_interest = 'true'
      const url = `/contracts/${contractId}/report/${kind}/`
      const res = await api.get(url, { params, responseType: 'blob', headers: authHeaders() })

      const isPdf = kind === 'pdf'
      const ok = isPdf
        ? (res.headers?.['content-type'] || '').includes('application/pdf')
        : (res.headers?.['content-type'] || '').includes('spreadsheetml')
      if (!ok) {
        let detail = ''
        try {
          const txt = await res.data?.text?.()
          try {
            const j = JSON.parse(txt)
            detail = j?.message || j?.detail || j?.error || ''
          } catch (_) {
            detail = txt ? String(txt).slice(0, 160) : ''
          }
        } catch (_) { /* blob خوانده نشد */ }
        setFileError(detail || `پاسخِ سرور ${isPdf ? 'PDF' : 'اکسل'} نبود.`)
        return
      }

      const blob = new Blob([res.data], {
        type: isPdf ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      })
      const url2 = window.URL.createObjectURL(blob)
      const cn = String(contract?.number || contractId).replace(/\//g, '_')
      if (isPdf) {
        // PDF: باز شدن در تبِ جدید (اگر پاپ‌آپ بلاک شد ⇒ دانلود اجباری)
        const win = window.open(url2, '_blank', 'noopener,noreferrer')
        if (!win) {
          const a = document.createElement('a')
          a.href = url2
          a.download = `contract_report_${cn}.pdf`
          document.body.appendChild(a)
          a.click()
          a.remove()
        }
      } else {
        const a = document.createElement('a')
        a.href = url2
        a.download = `contract_report_${cn}.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      setTimeout(() => window.URL.revokeObjectURL(url2), 60_000)
    } catch (e) {
      const status = e?.response?.status
      setFileError(
        status === 401 || status === 403
          ? 'نشست شما پایان یافته است؛ لطفاً خارج شوید و دوباره وارد شوید.'
          : 'ساخت فایل انجام نشد؛ لطفاً دوباره تلاش کنید.'
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Box>
      {/* ───── فیلترها ───── */}
      <Box className={classes.filters}>
        <Box className={classes.filterRow}>
          <Typography variant="body2" className={classes.filterLabel}>
            فیلتر بر اساس نوع:
          </Typography>
          {types.map((t) => (
            <Button
              key={t}
              size="small"
              variant="contained"
              className={type === t ? classes.chipActive : classes.chipBtn}
              onClick={() => setType(t)}
            >
              {toFarsiDigits(t)}
            </Button>
          ))}
        </Box>

        <Box className={classes.filterRow}>
          <Typography variant="body2" className={classes.filterLabel}>
            فیلتر بر اساس سال مالی:
          </Typography>
          {years.map((y) => (
            <Button
              key={y}
              size="small"
              variant="contained"
              className={String(year) === String(y) ? classes.chipActive : classes.chipBtn}
              onClick={() => setYear(y)}
            >
              {toFarsiDigits(y)}
            </Button>
          ))}
        </Box>

        <Box className={classes.filterRow}>
          <Typography variant="body2" className={classes.filterLabel}>
            فیلتر بر اساس سند:
          </Typography>
          {['همه', 'با سند', 'بدون سند'].map((d) => (
            <Button
              key={d}
              size="small"
              variant="contained"
              className={doc === d ? classes.chipActive : classes.chipBtn}
              onClick={() => setDoc(d)}
            >
              {d}
            </Button>
          ))}
        </Box>

        <Box className={classes.filterRow}>
          <Typography variant="body2" className={classes.filterLabel}>
            گزارش PDF:
          </Typography>
          <label className={withInterest ? classes.radioLabelActive : classes.radioLabel}>
            <input
              type="radio"
              name="panelInterest"
              checked={!withInterest}
              onChange={() => setWithInterest(false)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 13.5 }}>بدون حفظ قدرت خرید (نمایش سودها)</span>
          </label>
          <label className={withInterest ? classes.radioLabelActive : classes.radioLabel}>
            <input
              type="radio"
              name="panelInterest"
              checked={withInterest}
              onChange={() => setWithInterest(true)}
              style={{ width: 18, height: 18 }}
            />
            <span style={{ fontSize: 13.5 }}>با حفظ قدرت خرید (نمایش سودها)</span>
          </label>
        </Box>

        <Box className={classes.countRow}>
          <Typography variant="body2" style={{ color: '#666', fontStyle: 'italic' }}>
            تعداد نتایج: {toFarsiDigits(count)} مورد
          </Typography>
          <Box display="flex" style={{ gap: 10, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              className={classes.printBtn}
              onClick={() => downloadFile('pdf')}
              disabled={!count || busy}
              startIcon={busy ? <CircularProgress size={14} color="inherit" /> : null}
            >
              چاپ همه ({toFarsiDigits(count)} مورد)
            </Button>
            <Button
              variant="contained"
              className={classes.excelBtn}
              onClick={() => downloadFile('excel')}
              disabled={!count || busy}
            >
              دانلود اکسل ({toFarsiDigits(count)} مورد)
            </Button>
          </Box>
        </Box>
      </Box>

      {err && <Typography color="error">{err}</Typography>}
      {loading && (
        <Box textAlign="center" style={{ padding: 16 }}>
          <CircularProgress size={26} />
        </Box>
      )}

      {/* ───── جدولِ صورت وضعیت‌ها ───── */}
      {!loading && !err && (
        <>
          <ResponsiveTable
            columns={reportColumns}
            rows={visibleRows}
            showIndex
            indexLabel="ردیف"
            maxHeight={450}
            emptyText="صورت‌وضعیتی با این فیلترها یافت نشد."
          />
          <LoadMoreButton
            hasMore={hasMore}
            remaining={remaining}
            onClick={loadMore}
          />
        </>
      )}

      <Snackbar
        open={!!fileError}
        autoHideDuration={9000}
        onClose={() => setFileError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setFileError('')} severity="error" variant="filled">
          {fileError}
        </Alert>
      </Snackbar>
    </Box>
  )
}
